import { useEffect, useState, useCallback } from 'react';
import { API } from '@/store/api';

const SCORE_INCREMENT = 3;

export const useQuizPlay = (playerName: string, difficulty: string) => {
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionStatus, setQuestionStatus] = useState<Array<'unanswered' | 'correct' | 'incorrect'>>([]);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [playerId, setPlayerId] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  const [startQuiz, { isLoading, error }] = API.quizApi.useStartQuizMutation();

  const processLevels = (questions: Record<string, IQuizQuestion[]>) =>
    Object.entries(questions)
      .filter(([key]) => key.startsWith('level_'))
      .map(([_, levelQuestions], index) => ({
        level: index + 1,
        questions: levelQuestions.map((q) => ({
          ...q,
          image: {
            path: q.image?.path || '',
            mime_type: q.image?.mime_type || '',
            caption: q.image?.caption || '',
          },
        })),
      }));

  const initializeQuiz = useCallback(async () => {
    try {
      const result = await startQuiz({ name: playerName, difficulty }).unwrap();
      const { timer, player, questions } = result.data;
      const levels = processLevels(questions);

      setQuizData({
        playerName: player.name,
        levels,
        timer: timer.value,
      });

      setPlayerId(player.id);
      setQuestionStatus(Array(levels[0].questions.length).fill('unanswered'));
    } catch (err) {
      console.error('Error starting quiz:', err);
    }
  }, [startQuiz, playerName, difficulty]);

  useEffect(() => {
    initializeQuiz();
  }, [initializeQuiz]);

  useEffect(() => {
    if (quizCompleted) return;
    const timer = setTimeout(() => setElapsedTime((t) => t + 1), 1000);
    return () => clearTimeout(timer);
  }, [elapsedTime, quizCompleted]);

  const getNextNonEmptyLevelIndex = (index: number) =>
    quizData?.levels.findIndex((level, i) => i > index && level.questions.length > 0) ?? -1;

  const isLastQuestionAndLevel = () => {
    if (!quizData) return false;
    const currentLevel = quizData.levels[currentLevelIndex];
    const lastQuestion = currentQuestionIndex === currentLevel.questions.length - 1;
    const lastLevel = getNextNonEmptyLevelIndex(currentLevelIndex) === -1;
    return lastQuestion && lastLevel;
  };

  const handleAnswerClick = (selectedAnswer: string) => {
    if (!quizData || isAnswering) return;

    const currentLevel = quizData.levels[currentLevelIndex];
    const currentQuestion = currentLevel.questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct_answer;

    setIsAnswering(true);
    setQuestionStatus((prev) =>
      prev.map((status, i) => (i === currentQuestionIndex ? (isCorrect ? 'correct' : 'incorrect') : status)),
    );

    if (isCorrect) {
      setScore((prev) => prev + SCORE_INCREMENT);
      setCorrectAnswers((prev) => prev + 1);
    }

    setTimeout(() => {
      if (isLastQuestionAndLevel()) {
        setQuizCompleted(true);
      } else if (currentQuestionIndex === currentLevel.questions.length - 1) {
        const nextLevel = getNextNonEmptyLevelIndex(currentLevelIndex);
        if (nextLevel !== -1) {
          setCurrentLevelIndex(nextLevel);
          setCurrentQuestionIndex(0);
          setQuestionStatus(Array(quizData.levels[nextLevel].questions.length).fill('unanswered'));
        }
      } else {
        setCurrentQuestionIndex((i) => i + 1);
      }
      setIsAnswering(false);
    }, 2000);
  };

  return {
    quizData,
    questionStatus,
    currentLevelIndex,
    currentQuestionIndex,
    handleAnswerClick,
    isLoading,
    error,
    quizCompleted,
    score,
    correctAnswers,
    playerId,
    elapsedTime,
  };
};
